/* eslint camelcase: 0 */
// @ts-check

import last from 'lodash/last';
import isEqual from 'lodash/isEqual';
import {AttrsSchema, Component, ConfigOptions} from 'panel';
import type {Point, SeriesLineOptions} from 'highcharts';

import {commaizeNumber, htmlEncodeString} from 'common/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import type {BarChartPopover} from 'common/report/funnels/visualization/chart-display';
import {SelectedSegmentStep, TimeBucketDefinition, TimeBucketKey} from 'common/report/funnels/models/bar-chart/types';
import {
  getBucketAccessor,
  getBucketLabel,
  getFormattedUserPct,
  getMedianAccessor,
  getTTCShiftedEventIndex,
  labelFormatter,
} from 'common/report/funnels/models/time-to-convert/utils';
import {formatTime} from 'common/util/date';
import {
  formatSumOfSingleBucketData,
  formatXAxis,
  formatYAxisLabel,
  getBucketData,
  getFunnelStep,
  getLineChartDataforBucketReports,
  getToolTip,
  percentOfTotal,
} from 'common/report/funnels/models/bar-chart/utils';
import {DisplayAnalysisType, LineStyle} from 'common/charts/visualization/line-chart/enums';
import {ChartType, REPORT_LIMITS, ReportName, SavedLegend, ValueRepresentationType} from 'common/report/constants';
import {FunnelBookmark, FunnelSegment, FunnelStep} from 'common/report/funnels/models/types';
import {createBucketLineChartFunnelData, getCalendarDateRangeLabels} from 'common/report/funnels/models/utils';
import {FunnelQueryResponse} from 'common/report/funnels/models/api-types';
import {SegmentFilterController, SegmentItem} from 'common/charts/contexts/segment-filter-controller';
import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {processFunnelForChartWidget} from 'common/report/funnels/models/aggregates';
import {LineChartAttrs} from 'common/charts/visualization/line-chart';
import {getOverallStepsFromFunnelSegment} from 'common/report/funnels/util';
import {FunnelsQuery} from 'common/report/funnels/models/funnels-query';
import {getBookmarkWithAppliedDefaults} from 'common/report/funnels/util/bookmark-overrides';
import {CurrentProject, CurrentProjectResource} from 'common/report/model-resources/projects';

import {template} from './template';
import css from './index.inline.styl';

interface ChartState {
  mappedFunnelData: Map<string, SelectedSegmentStep>;
  mappedComparisonData: Map<string, SelectedSegmentStep>;
  segmentData: FunnelSegment[];
  comparisonSegmentData: FunnelSegment[];
  funnelQuery: FunnelsQuery;
}

interface ChartAttrs {
  'include-header'?: boolean;
  'hide-tooltip'?: boolean;
  'enable-point-click'?: boolean;
}

export class FunnelTimeToConvertChart extends Component<ChartState, ChartAttrs, any, any, ChartsContextRegistry> {
  private _funnelsResponse: FunnelQueryResponse;
  private _bookmark: FunnelBookmark;
  private _projectDetails: any | null = null;

  static get attrsSchema(): AttrsSchema<ChartAttrs> {
    return {
      ...super.attrsSchema,
      'include-header': `boolean`,
      'hide-tooltip': `boolean`,
      'enable-point-click': `boolean`,
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.processData();
  }

  resourceCustomSubscriptions = new Map([[CurrentProjectResource, () => !this._projectDetails && this.processData()]]);

  private getCurrentProject(): CurrentProject {
    return this.getContext(`currentProject`).get().value;
  }

  get config(): ConfigOptions<ChartState, any, ChartsContextRegistry> {
    return {
      template,
      css,
      contexts: [`segmentFilterController` as const, `currentProject` as const],
      defaultContexts: {
        segmentFilterController: new SegmentFilterController(),
      },
      useShadowDom: true,
      defaultState: {
        mappedFunnelData: null,
        mappedComparisonData: null,
        comparisonSegmentData: [],
        segmentData: [],
        funnelQuery: null,
      },
      helpers: {
        ValueRepresentationType,
        // always hidden for first step
        isHidden: () => this.stepIndex === 0,
        hasData: () => {
          const stepData = this.state.segmentData?.[0]?.steps;
          if (!stepData || stepData.length === 0) {
            return false;
          }

          if (this.stepIndex === 0) {
            return false;
          }

          const lastStep: FunnelStep = last(stepData);
          return this.stepIndex ? TimeBucketKey.PREV in stepData[this.stepIndex] : TimeBucketKey.START in lastStep;
        },
        isDrilledDown: () => this.isDrilledDown,
        subtitleText: () => {
          const funnelStep = getFunnelStep(this.getRepresentativeSteps(), this.stepIndex);
          if (!funnelStep) {
            return null;
          }
          const bucketData = getBucketData(funnelStep, getBucketAccessor(this.stepIndex));
          const conversions = `${formatSumOfSingleBucketData(bucketData)} conversions`;
          return {
            conversionText: this.isDrilledDown ? `${conversions} in ${this.timeSelection}` : conversions,
            totalConversionPercentageText: `${commaizeNumber(funnelStep.count)} total (${getFormattedUserPct(
              funnelStep,
              getBucketAccessor(this.stepIndex),
            )}%)`,
          };
        },

        /** Line Chart Methods */
        isSegmented: () => this.state.segmentData?.length > 1,
        lineChartData: () => {
          const bucketAccessor = getBucketAccessor(this.stepIndex);
          const segmentColors = this.getSelectedSegmentColors();
          let chartData = getLineChartDataforBucketReports({
            segmentData: this.state.segmentData,
            stepIndex: this.stepIndex,
            bucketAccessor: bucketAccessor as keyof FunnelStep,
            segmentColors,
            isComparison: false,
            representationType: this.ttcValueRepresentation,
          });
          if (this.hasComparison()) {
            chartData = [
              ...chartData,
              ...getLineChartDataforBucketReports({
                segmentData: this.state.comparisonSegmentData,
                stepIndex: this.stepIndex,
                bucketAccessor: bucketAccessor as keyof FunnelStep,
                segmentColors,
                isComparison: true,
                representationType: this.ttcValueRepresentation,
              }),
            ];
          }
          return chartData;
        },
        lineChartClick: (point: Point) => {
          const {series, index, color, plotX: clientX, plotY: clientY} = point;
          if ((series.options as SeriesLineOptions).dashStyle === LineStyle.Comparison) {
            return;
          }
          const {name: segmentKey} = series;
          const {step: funnelStep, segmentName} = this.state.mappedFunnelData.get(segmentKey);
          const bucketData = getBucketData(funnelStep, getBucketAccessor(this.stepIndex));
          const buckets = bucketData.buckets;
          const count = buckets[index];
          const selection: BarChartPopover = {
            segment: {
              color: color as string,
              count,
              // @ts-ignore (maybe we don't need this property) Object literal may only specify known properties
              percentage: percentOfTotal(bucketData, count),
              isSelected: true,
              isClickable: true,
              label: segmentName,
              name: segmentKey,
            },
            rowLabel: formatXAxis(index, this.timeBuckets, getBucketLabel),
            queryIndex: index,
            clientX,
            clientY,
            point,
          };

          this.dispatchEvent(
            new CustomEvent(`openPopover`, {
              detail: {
                selection,
              },
            }),
          );
        },
        formatTooltip: (x: number, _y: number, series: any, _idx: number) => {
          const {color, name: seriesName} = series;
          if (this.attr(`hide-tooltip`)) {
            return ``;
          }
          let titleColor = ``;

          if (!this.hasComparison()) {
            titleColor = `title-color=${color}`;
          }

          const toolTipAttrs = `${titleColor} sections="${htmlEncodeString(
            JSON.stringify(
              getToolTip(
                formatXAxis(x, this.timeBuckets, getBucketLabel, true),
                seriesName,
                x,
                false,
                labelFormatter,
                this.state.mappedFunnelData,
                this.state.mappedComparisonData,
                getBucketAccessor(this.stepIndex) as keyof FunnelStep,
                getMedianAccessor(this.stepIndex) as keyof FunnelStep,
                this.getDateRangeLabel(),
                undefined,
                undefined,
                color,
              ),
            ),
          )}"`;

          return `
                <mp-chart-tooltip ${toolTipAttrs}>
              `;
        },
        formatXAxisLabel: (value: number) => {
          return formatXAxis(value, this.timeBuckets, getBucketLabel);
        },
        formatYAxisLabel: (value: number) => {
          return formatYAxisLabel(value, this.ttcValueRepresentation);
        },

        getLineChartAttrs: (): Partial<LineChartAttrs> => {
          const projectDetails = this.getProjectDetails();
          return {
            'chart-width': null,
            'display-analysis-type': DisplayAnalysisType.Linear,
            timezone: projectDetails.projectTimeZone,
            'utc-offset': projectDetails.utcOffset,
            'zoom-type': `x`,
            ...(this.ttcValueRepresentation === ValueRepresentationType.Relative
              ? {'y-axis-options': {maxPadding: 0}}
              : {}),
          };
        },

        getLineChartProps: () => {
          return {
            chartData: this.helpers.lineChartData(),
            formatYAxisLabel: this.helpers.formatYAxisLabel,
            formatXAxisLabel: this.helpers.formatXAxisLabel,
            formatTooltip: this.helpers.formatTooltip,
            onHighChartClick: this.attr(`enable-point-click`) && this.helpers.lineChartClick,
          };
        },
      },
    };
  }

  get isDrilledDown() {
    return this.timeBuckets && this.timeBuckets.isDrilledDown;
  }

  get ttcValueRepresentation() {
    return this._bookmark.ttcValueRepresentation || ValueRepresentationType.Relative;
  }

  /** Currently selected conversion time window, formatted */
  get timeSelection() {
    const timeBuckets = this.timeBuckets;
    if (!timeBuckets.max) {
      return null;
    }

    return formatTime(timeBuckets.min, timeBuckets.max);
  }

  hasComparison() {
    return this.state.comparisonSegmentData?.length > 0;
  }

  updateFunnelChartData(formattedData: FunnelSegment[], formattedCompareData: FunnelSegment[]) {
    const selectedSegmentColors = this.getSelectedSegmentColors();

    const mappedFunnelData = createBucketLineChartFunnelData({
      segmentData: formattedData,
      selected: selectedSegmentColors,
      stepIndex: this.stepIndex,
      hasComparison: this.hasComparison(),
      isComparisonData: false,
    });

    const mappedComparisonData = formattedCompareData
      ? createBucketLineChartFunnelData({
          segmentData: formattedCompareData,
          selected: selectedSegmentColors,
          stepIndex: this.stepIndex,
          hasComparison: !!formattedCompareData,
          isComparisonData: true,
        })
      : null;

    this.update({
      mappedFunnelData,
      mappedComparisonData,
      segmentData: formattedData,
      comparisonSegmentData: formattedCompareData,
    });
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  public set funnelsResponse(response: FunnelQueryResponse) {
    if (!isEqual(this._funnelsResponse, response)) {
      this._funnelsResponse = response;
      this.processData();
    }
  }

  public get funnelsResponse(): FunnelQueryResponse {
    return this._funnelsResponse;
  }

  public set bookmark(bookmark: FunnelBookmark) {
    const updatedBookmark = getBookmarkWithAppliedDefaults(bookmark);
    if (!isEqual(this._bookmark, updatedBookmark)) {
      this._bookmark = updatedBookmark;
      this.processData();
    }
  }

  public get bookmark(): FunnelBookmark {
    return this._bookmark;
  }

  public set projectDetails(value) {
    if (!isEqual(this._projectDetails, value)) {
      this._projectDetails = value;
      this.processData();
    }
  }
  get stepIndex() {
    return getTTCShiftedEventIndex(this._bookmark.eventIndex);
  }

  getDateRangeLabel() {
    const {projectAsOfDate, projectTimeZone} = this.getProjectDetails();
    return getCalendarDateRangeLabels(this._bookmark, this.funnelsResponse, projectAsOfDate, projectTimeZone);
  }

  get timeBuckets(): TimeBucketDefinition {
    const bookmarkTimeBucket = this._bookmark.time_buckets;
    return {
      min: bookmarkTimeBucket.min,
      max: bookmarkTimeBucket.max,
      bucketSize: bookmarkTimeBucket.bucket_size,
      isDrilledDown: bookmarkTimeBucket.is_drilled_down,
      offset: bookmarkTimeBucket.offset,
    };
  }
  processData() {
    if (!this._bookmark || !this._funnelsResponse || !this.isConnected) {
      return;
    }

    const projectDetails = this.getProjectDetails();

    const {legendSegmentItems, savedLegend, stateUpdates} = processFunnelForChartWidget(
      this._bookmark,
      this.funnelsResponse,
      projectDetails.projectId,
      projectDetails.projectAsOfDate,
      this.stepIndex,
      getMedianAccessor(this.stepIndex),
      projectDetails.projectTimeZone,
    );

    const {formattedCompareData, formattedData, query: funnelQuery} = stateUpdates;

    this.update({funnelQuery});

    this.updateSegmentFilterController(legendSegmentItems, savedLegend);
    this.updateFunnelChartData(formattedData, formattedCompareData);
  }

  private getProjectDetails() {
    if (this._projectDetails) {
      return this._projectDetails;
    }
    const currentProject = this.getCurrentProject();
    if (currentProject) {
      return {
        projectTimeZone: currentProject.timezone,
        projectAsOfDate: currentProject.projectAsOfDate,
        utcOffset: currentProject.utcOffset,
        projectId: currentProject.id.toString(),
      };
    }
    return null;
  }

  getSelectedSegmentColors() {
    return this.segmentFilterController
      .getSelectedItems(0)
      .map((seg) => ({key: seg.id, color: this.segmentFilterController.getItemColor(0, seg.id)}));
  }

  getRepresentativeSteps() {
    return !this.state.funnelQuery?.isGroupedSteps() ? getOverallStepsFromFunnelSegment(this.state.segmentData) : [];
  }

  private updateSegmentFilterController(legendSegmentItems: SegmentItem[], savedLegend: SavedLegend[]) {
    this.segmentFilterController.loadSections([{items: legendSegmentItems}], {
      defaultNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCLine].default,
      maxNumSelections: REPORT_LIMITS[ReportName.Funnels].segments[ChartType.FunnelTTCLine].max,
    });
    this.segmentFilterController.loadFromSavedLegend(savedLegend);
  }
}

export const FunnelsTTCLineChartEl = defineAndMakeJsxFactory(`mp-funnels-ttc-line-chart`, FunnelTimeToConvertChart);
export const FunnelsTTCLineChartReact = panel2react(`mp-funnels-ttc-line-chart`, FunnelTimeToConvertChart);
